import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService, { specializedAttachFormats } from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { objectField, dateRangeField, boolField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import CsvReportGrid from '@components/reports/CsvReportGrid'

const TYPE_USERS = 1
const TYPE_COMPANIES = 3

// Spec 203: the run is legacy's own file (CB's TranslateToReport), so the grid shows
// legacy's summary or detail layout and its totals. The dispatcher's raw split lines
// this page used to receive repeated each payment on every split, and summing them
// overstated Applied Payment 12.08x on vladdemo.
const EMPTY = { headers: [], rows: [], meta: [] }

// NOTE the PascalCase wire keys — this filter class is one of the few that uses
// them, so the specs below are the only place they appear and the wire test pins
// them. `IsARReport` has no control and must survive every edit (it selects a
// different report mode server-side).
const FIELDS = [
  objectField({ key: 'client', label: 'Client', typeId: TYPE_COMPANIES, idKey: 'ClientId', nameKey: 'ClientName' }),
  objectField({ key: 'attorney', label: 'Attorney', typeId: TYPE_USERS, idKey: 'UserId', nameKey: 'UserName' }),
  dateRangeField({ key: 'dates', label: 'Date Range', startKey: 'DateStart', endKey: 'DateEnd', omitAll: true }),
  // Changes which rows the proc returns and requires a re-run, so it is a filter
  // and stays in the bar. Renders as a Yes/No select rather than the old
  // checkbox — the shared registry's BOOL widget (CC-49925).
  boolField({ key: 'detail', label: 'Show detail', valueKey: 'IsShowDetail' }),
]

const makeDefaults = () => ({
  ClientId: null,
  ClientName: '',
  UserId: null,
  UserName: '',
  DateStart: dayjs().startOf('month').format('YYYY-MM-DD'),
  DateEnd: dayjs().format('YYYY-MM-DD'),
  IsShowDetail: false,
  IsARReport: false,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const FeeAllocationReport = ({ reportId, name = 'Fee Allocation by Attorney', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun, invalidate: invalidateRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.DateStart, filters.DateEnd)), [filters])
  const [data, setData] = useState(EMPTY)
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const runReport = useCallback(() => {
    if (!filters.DateStart || !filters.DateEnd) {
      // Spec 131: the refusal also retires a run still in flight.
      invalidateRun({ error: 'Date range is required.' })
      return undefined
    }
    return startRun(async () => {
      const jid = jobRef.current
      jobRef.current = null // consumed — subsequent runs are fresh
      if (jid) {
        // Tray "View report" → reopen the exact saved snapshot + show its run time.
        const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
        return () => { setSavedAsOf(st?.createdAt || null); setData(r?.data || EMPTY) }
      }
      const result = await specializedReportService.feeAllocation.run(filters, { reportId })
      // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
      return () => { setSavedAsOf(null); setData(result || EMPTY) }
    }, { criteria: { filtersSummary } })
  }, [filters, reportId, startRun, invalidateRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'fee-allocation',
        endpoint: (f) => ({ method: 'POST', path: '/reports/fee-allocation', body: f }),
        // Spec 206: its emailed file is its own export, the run's request.
        attachFormats: specializedAttachFormats('/reports/fee-allocation'),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:fee-allocation"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
      download={(format, getToken) =>
        specializedReportService.feeAllocation.download(filters, format, getToken, `fee-allocation.${format}`)
      }
    >
      <CsvReportGrid key={view.gridKey} data={data} storageKey="report:fee-allocation" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default FeeAllocationReport
